import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import './DiscussionDetails.scss';
import { getAPIUrl } from '../api/apiUtils';
import AnomalityBadge from '../components/AnomalityBadge';
import PostList from '../components/PostList';
import PostMeta from '../components/PostMeta';
import ExportModal from '../components/ExportModal';
import { truncateText, formatVkLink, formatDate } from '../utils/formatUtils';
import { useGlobalFilters, useGlobalFiltersSubscription } from '../hooks/useGlobalFilters';

// Функция для получения URL изображения из вложений
const getAttachmentImage = (attachments) => {
  if (!attachments || !attachments.length) {
    console.log('No attachments found at', new Date().toLocaleString('ru-RU', { timeZone: 'Europe/Berlin' }));
    return '';
  }
  const photo = attachments.find((att) => att.type === 'photo');
  if (!photo || !photo.photo) {
    console.log('No valid photo object found at', new Date().toLocaleString('ru-RU', { timeZone: 'Europe/Berlin' }), ':', photo);
    return '';
  }
  const sizes = photo.photo.sizes || [];
  const url = sizes.find((size) => size.type === 'x')?.url || sizes[sizes.length - 1]?.url || '';
  console.log('Extracted Image URL at', new Date().toLocaleString('ru-RU', { timeZone: 'Europe/Berlin' }), ':', url);
  return url;
};

// Функция для получения данных об аномальности
const fetchAnomalityData = async (groupIds, startDate, endDate) => {
  if (!groupIds || groupIds.length === 0) {
    return {};
  }
  
  try {
    const params = {
      page: 1,
      limit: 10000, // Получаем больше данных для сопоставления
      columns: 'group_id,post_id,text_anomality',
      group_ids: groupIds.join(','),
      start_date: startDate,
      end_date: endDate,
    };

    const response = await axios.get(`${getAPIUrl()}/detection/text_anomality_analysis`, { params });
    const anomalityData = response.data.data || [];
    
    // Создаем карту для быстрого поиска по group_id и post_id
    const anomalityMap = {};
    anomalityData.forEach(item => {
      const key = `${item.group_id}-${item.post_id}`;
      anomalityMap[key] = item.text_anomality;
      console.log(key, item.text_anomality);
    });
    console.log('get anomality data');
    return anomalityMap;
  } catch (error) {
    console.error('Ошибка загрузки данных об аномальности:', error);
    return {};
  }
};



const DiscussionDetails = () => {
  const filtersStore = useGlobalFilters();
  const postsRequestId = useRef(0);
  const filtersKeyRef = useRef(JSON.stringify(filtersStore.filters));

  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(() => filtersStore.selectedGroups.length > 0);
  const [postsError, setPostsError] = useState('');
  const [selectedPost, setSelectedPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [totalComments, setTotalComments] = useState(0);
  const [totalPosts, setTotalPosts] = useState(0);
  const [postsPage, setPostsPage] = useState(1);
  const [commentsPage, setCommentsPage] = useState(1);
  const [hasMorePosts, setHasMorePosts] = useState(true);
  const [expandedPosts, setExpandedPosts] = useState({});
  const [expandedComments, setExpandedComments] = useState({});
  
  // Состояния для модальных окон экспорта
  const [showPostsExport, setShowPostsExport] = useState(false);
  const [showCommentsExport, setShowCommentsExport] = useState(false);

  const commentsPerPage = 10;

  // Используем современный подход с MobX реактивностью
  const [globalFilters, setGlobalFilters] = useState(filtersStore.filters);
  
  // Подписываемся на изменения фильтров через MobX
  const handleFiltersChange = useCallback((filters) => {
    const nextKey = JSON.stringify(filters);
    if (nextKey === filtersKeyRef.current) return;
    filtersKeyRef.current = nextKey;
    setPostsPage(1);
    setGlobalFilters(filters);
    setLoadingPosts(filters.selectedGroups.length > 0);
    setPostsError('');
  }, []);
  useGlobalFiltersSubscription(handleFiltersChange);

  // Загрузка постов с учетом фильтров из store и локальных фильтров
  const fetchPosts = useCallback(async (page) => {
    const requestId = ++postsRequestId.current;
    if (!globalFilters.selectedGroups || globalFilters.selectedGroups.length === 0) {
      setPosts([]);
      setPostsError('');
      setLoadingPosts(false);
      return;
    }
    setLoadingPosts(true);
    setPostsError('');
    try {
      const params = { group_ids: globalFilters.selectedGroups.join(','), page, limit: 10 };
      if (globalFilters.minLikes) params.minLikes = parseInt(globalFilters.minLikes);
      if (globalFilters.minViews) params.minViews = parseInt(globalFilters.minViews);
      if (globalFilters.minComments) params.minComments = parseInt(globalFilters.minComments);
      if (globalFilters.startDate) params.startDate = globalFilters.startDate;
      if (globalFilters.endDate) params.endDate = globalFilters.endDate;

      const res = await axios.get(`${getAPIUrl()}/vk/collection/multiple`, { params });
      if (requestId !== postsRequestId.current) return;
      const postsData = Array.isArray(res.data) ? res.data : res.data.items || [];
      setPosts(postsData.map((post) => ({ ...post, text_anomality: null })));
      setLoadingPosts(false);
      if (postsData.length > 0) {
        fetchAnomalityData(globalFilters.selectedGroups, globalFilters.startDate, globalFilters.endDate)
          .then((anomalityMap) => {
            if (requestId !== postsRequestId.current) return;
            setPosts((currentPosts) => currentPosts.map((post) => ({
              ...post,
              text_anomality: anomalityMap[`${post.group_id}-${post.id}`] ?? null,
            })));
          });
      }
      setHasMorePosts(postsData.length === 10);
      
      // Приблизительная оценка общего количества постов
      // Если это первая страница и есть еще посты, оцениваем общее количество
      if (page === 1) {
        if (postsData.length < 10) {
          setTotalPosts(postsData.length);
        } else {
          // Если получили полную страницу, предполагаем, что постов больше
          setTotalPosts(postsData.length + 1); // Минимум на 1 больше
        }
      } else {
        // Для последующих страниц обновляем оценку
        if (postsData.length < 10) {
          setTotalPosts((page - 1) * 10 + postsData.length);
        } else {
          setTotalPosts(page * 10 + 1);
        }
      }
    } catch (error) {
      console.error('Ошибка загрузки постов:', error);
      if (requestId === postsRequestId.current) {
        setPosts([]);
        setPostsError('Не удалось загрузить посты. Попробуйте изменить фильтры или обновить страницу.');
        setLoadingPosts(false);
      }
    }
  }, [globalFilters]);

  useEffect(() => {
    setSelectedPost((current) =>
      current && posts.some((post) => post.id === current.id && post.group_id === current.group_id)
        ? current
        : posts[0] || null
    );
  }, [posts]);
  
  // Загрузка комментариев
  const fetchComments = useCallback(async (page) => {
    if (!selectedPost) return;
    setLoadingComments(true);
    try {
      const res = await axios.get(
        `${getAPIUrl()}/vk/collection/${selectedPost.group_id}/post/${selectedPost.id}/comments`,
        { params: { page, limit: commentsPerPage } }
      );
      const commentsData = res.data.comments || [];
      setComments(commentsData);
      setTotalComments(res.data.total || selectedPost.comments_count || 0);
    } catch (error) {
      console.error('Ошибка загрузки комментариев:', error);
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  }, [selectedPost]);

  // Функция для получения данных об аномальности для выбранного поста
  const fetchSelectedPostAnomality = useCallback(async (post) => {
    if (!post) return post;
    
    try {
      const params = {
        page: 1,
        limit: 1,
        columns: 'text_anomality',
        group_ids: post.group_id,
      };
      
      const response = await axios.get(`${getAPIUrl()}/detection/text_anomality_analysis`, { params });
      const anomalityData = response.data.data || [];
      
      // Ищем данные для конкретного поста
      const postAnomality = anomalityData.find(item => 
        item.group_id === post.group_id && item.post_id === post.id
      );
      
      return {
        ...post,
        text_anomality: postAnomality?.text_anomality || null
      };
    } catch (error) {
      console.error('Ошибка загрузки данных об аномальности для выбранного поста:', error);
      return post;
    }
  }, []);

  // Обработчик выбора поста с загрузкой данных об аномальности
  const handlePostSelect = useCallback(async (post) => {
    const postWithAnomality = await fetchSelectedPostAnomality(post);
    setSelectedPost(postWithAnomality);
  }, [fetchSelectedPostAnomality]);

  // Следим за изменением фильтров и страниц
  useEffect(() => {
    fetchPosts(postsPage);
  }, [postsPage, fetchPosts]);

  useEffect(() => {
    if (selectedPost) fetchComments(commentsPage);
    else setComments([]);
  }, [selectedPost, commentsPage, fetchComments]);

  // Переключение страниц
  const handlePostsPageChange = (newPage) => {
    if (newPage > 0 && (newPage === 1 || hasMorePosts)) setPostsPage(newPage);
  };

  const handleCommentsPageChange = (newPage) => {
    const totalPages = Math.ceil(totalComments / commentsPerPage);
    if (newPage > 0 && newPage <= totalPages) setCommentsPage(newPage);
  };




  const togglePostText = (postId) => setExpandedPosts((prev) => ({ ...prev, [postId]: !prev[postId] }));
  const toggleCommentText = (commentIndex) => setExpandedComments((prev) => ({ ...prev, [commentIndex]: !prev[commentIndex] }));

  // Рендеринг пагинации комментариев
  const renderCommentsPagination = () => {
    const totalPages = Math.ceil(totalComments / commentsPerPage);
    if (totalPages <= 1) return null;

    const pages = [];
    const range = 2;
    const start = Math.max(1, commentsPage - range);
    const end = Math.min(totalPages, commentsPage + range);

    for (let i = start; i <= end; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handleCommentsPageChange(i)}
          className={i === commentsPage ? 'source-data-active' : ''}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="source-data-pagination">
        <button onClick={() => handleCommentsPageChange(1)} disabled={commentsPage === 1}>
          В начало
        </button>
        {start > 1 && <span>...</span>}
        {pages}
        {end < totalPages && <span>...</span>}
        <button onClick={() => handleCommentsPageChange(totalPages)} disabled={commentsPage === totalPages}>
          В конец
        </button>
      </div>
    );
  };

  return (
    <div className="source-data">
      {/* Модальные окна экспорта */}
      <ExportModal
        isOpen={showPostsExport}
        onClose={() => setShowPostsExport(false)}
        dataType="posts"
        title="Экспорт постов"
      />
      <ExportModal
        isOpen={showCommentsExport}
        onClose={() => setShowCommentsExport(false)}
        dataType="comments"
        title="Экспорт комментариев"
      />

      <div className="source-data-container">
        {/* Посты */}
        <div className="source-data-column source-data-posts-column">
          <div className="source-data-header">
            <h2>Посты</h2>
            {posts.length > 0 && !loadingPosts && (
            <button               
              onClick={() => setShowPostsExport(true)}              
              style={{
                padding: "10px 14px",
                background: "#f4f6f8",
                color:  "#222",
                border: "1px solid #d1d5db",            
                borderRadius: 8,              
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 15,
                fontWeight: 500,
              }}
              onMouseOver={(e) => {                
                e.currentTarget.style.background = "#e5e7eb";                
                e.currentTarget.style.boxShadow = "0 4px 16px #3c3c3c3a";
              }}
              onMouseOut={(e) => {              
                e.currentTarget.style.background = "#f4f6f8";
                e.currentTarget.style.boxShadow = "0 0px 0px #3c3c3c3a";              
              }}      
            >
              Экспорт
            </button>
            )}
          </div>
          {loadingPosts ? (
            <p role="status" className="discussion-details-status">Загрузка постов...</p>
          ) : postsError ? (
            <p role="alert" className="discussion-details-status discussion-details-status--error">{postsError}</p>
          ) : posts.length === 0 ? (
            <p className="discussion-details-status">
              {globalFilters.selectedGroups.length === 0 ? 'Выберите сообщества в верхней панели' : 'За выбранный период посты не найдены'}
            </p>
          ) : (
          <PostList
            posts={posts}
            handlePostSelect={handlePostSelect}
            togglePostText={togglePostText}
            expandedPosts={expandedPosts}
            handlePostsPageChange={handlePostsPageChange}
            postsPage={postsPage}
            hasMorePosts={hasMorePosts}
            totalPosts={totalPosts}
            pageSize={10}
          />
          )}
        </div>
        {/* Комментарии и детали поста */}
        <div className="source-data-column source-data-comments-column">
          <div className="source-data-header">
            <h2>Комментарии и детали поста</h2>
            {comments.length > 0 && !loadingComments && (
            <button               
              onClick={() => setShowCommentsExport(true)}   
              style={{
                padding: "10px 14px",
                background: "#f4f6f8",
                color:  "#222",
                border: "1px solid #d1d5db",            
                borderRadius: 8,              
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 15,
                fontWeight: 500,
              }}
              onMouseOver={(e) => {                
                e.currentTarget.style.background = "#e5e7eb";                
                e.currentTarget.style.boxShadow = "0 4px 16px #3c3c3c3a";
              }}
              onMouseOut={(e) => {              
                e.currentTarget.style.background = "#f4f6f8";
                e.currentTarget.style.boxShadow = "0 0px 0px #3c3c3c3a";              
              }}           
            >
              Экспорт
            </button>
            )}
          </div>
          {selectedPost ? (
            <>
              <div className="source-data-selected-post">
                <div className="source-data-selected-post-header">
                  <h3>Выбранный пост</h3>
                  {selectedPost.text_anomality !== null && (
                  <AnomalityBadge anomality={selectedPost.text_anomality} size="medium" />
                  )}
                </div>
                <p>{selectedPost.text || 'Нет текста'}</p>
                {(() => {
                  const imageUrl = getAttachmentImage(selectedPost.attachments);
                  return imageUrl && (
                    <img
                      src={imageUrl}
                      alt="Изображение поста"
                      className="source-data-post-image"
                      onLoad={() => console.log('Image loaded successfully at', new Date().toLocaleString('ru-RU', { timeZone: 'Europe/Berlin' }), ':', imageUrl)}
                      onError={(e) => console.log('Image load error at', new Date().toLocaleString('ru-RU', { timeZone: 'Europe/Berlin' }), ':', e.target.src)}
                    />
                  );
                })()}

                <PostMeta post={selectedPost} />

              </div>
              <h3>Комментарии</h3>
              {loadingComments ? (
                <p role="status" className="discussion-details-status">Загрузка комментариев...</p>
              ) : comments.length > 0 ? (
                <>
                  {comments.map((comment, index) => (
                    <div key={index} className="source-data-comment">
                      <p onClick={() => toggleCommentText(index)}>
                        {expandedComments[index] ? comment.text : truncateText(comment.text)}
                        {comment.text?.split(' ').filter((word) => word.length > 0).length > 10 && (
                          <span className="source-data-read-more">
                            {expandedComments[index] ? ' Свернуть' : ' Читать далее'}
                          </span>
                        )}
                      </p>
                      <div className="source-data-comment-meta">
                        <span>📅 {comment.formatted_date || formatDate(comment.date)}</span>
                        <span>👍 {comment.likes || 0}</span>
                        <a href={comment.profile_link || formatVkLink(comment.from_id)} target="_blank" rel="noopener noreferrer">Автор</a>
                      </div>
                    </div>
                  ))}
                  {renderCommentsPagination()}
                </>
              ) : (
                <p>Нет комментариев</p>
              )}
            </>
          ) : (
            <p>Выберите пост для просмотра деталей и комментариев</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiscussionDetails;
